import { ActivityEntityName } from './activity.entity.name';
import { EntityStatus, EntitySubStatus } from '../entity.status/entity.status';
import { EntityStatusType, Party } from '../entity.status/enum';
import { GeneralSetting, isStageAndItemDeletionPrevented } from '../settings/general.settings';
import { Task } from '../tasks/task';
import { TaskLinkType } from '../tasks/task.enum';
import { canEditInStatus } from '../tasks/task.status.policy';
import { CodeType, isSorCode } from './code.type';
import { canEditAsAuthor, ItemCreator } from './item.creator';
import { SorItemFields } from './sor.item';

/**
 * Whether the line's own status lets this caller change its fields (SP-7).
 *
 * One predicate for both readers: the capability the editor is gated on and the pipeline guard that
 * refuses the write. Two copies of it would let the editor offer a field the write then refuses.
 *
 * A line whose status cannot be resolved — stamped before the cutover, or holding a status the set no
 * longer has — keeps the line-level answer rather than reading as unlocked.
 */
export function canEditItemInStatus(status: EntityStatus | null | undefined, party: Party, creator?: ItemCreator | null, companyId?: string): boolean {

    if (!status) return true;

    // An absent list is a status nobody has configured yet, not a locked one; only an explicitly empty
    // list locks the record. Reading absence as a lock would shut every status a tenant never edited.
    if (status.editableBy && !canEditInStatus(status, party)) return false;

    return !hasAuthorWindow(status) || canEditAsAuthor(creator, party, companyId);
}

/**
 * Whether the line's own status lets this caller remove it.
 *
 * An authorised status's `editableBy` locks the line's values, not the line: Cancelled names no editor,
 * and reading that as a delete lock refused a line the Items (old) tab still deletes (WBS-12265). A
 * pending status keeps its edit window, which is what keeps a Requested line from the side that raised it.
 */
export function canDeleteItemInStatus(status: EntityStatus | null | undefined, party: Party, creator?: ItemCreator | null, companyId?: string): boolean {

    return status?.entityType !== ActivityEntityName.ItemPending || canEditItemInStatus(status, party, creator, companyId);
}

/**
 * Whether the tenant's Prevent deletion refuses this line.
 *
 * A Draft has not been requested, so nothing has been reviewed, costed or claimed against it: removing
 * one added by mistake loses no record, and Cancel would leave a Declined line nobody declined (WBS-12205).
 */
export function isItemDeletionPrevented(settings: Pick<GeneralSetting, 'operations'> | null | undefined, status: Pick<EntityStatus, 'entityType' | 'isDefault'> | null | undefined): boolean {

    return isStageAndItemDeletionPrevented(settings) && !hasAuthorWindow(status);
}

/** Whether only the party that raised a line may edit it here; the Item set's default is Original, which an authorised line never leaves. */
export function hasAuthorWindow(status: Pick<EntityStatus, 'entityType' | 'isDefault'> | null | undefined): boolean {

    return status?.entityType === ActivityEntityName.ItemPending && !!status.isDefault;
}

/**
 * Whether a pending line in this status may be approved (SP-8).
 *
 * Requested and any Active review tier a tenant added, never the set default — a line nobody has
 * requested yet is not up for approval — and never an Inactive one, which is already resolved.
 *
 * Takes either level because the pickers still read the flattened seam, which projects a converted
 * set's parent into the sub-status shape. On a set the migration has not reached, `isDefault` there
 * still means the default reason; those tenants have no Draft to confuse it with.
 */
export function canApproveInStatus(status: EntityStatus | EntitySubStatus | null | undefined): boolean {

    return status?.statusType === EntityStatusType.Active && status?.isDefault !== true;
}

// Keyed on leaving Draft, not on reaching Requested, so a tenant review tier is gated too; an unresolved status is gated because it cannot prove it already left (EV-40).
export function isRequestTransition(from: EntityStatus | null | undefined, target: Pick<EntityStatus, '_id' | 'statusType' | 'isDefault'>): boolean {

    if (target.statusType !== EntityStatusType.Active || target.isDefault || target._id === from?._id) return false;

    return !from || hasAuthorWindow(from);
}

// Draft and Requested both leave the line's task Pending, so only the line's own status says the office is reviewing it; no author window, since any side may add a Draft's photos (WBS-11979).
export function findPendingLineTaskRefusal(lineStatus: EntityStatus | null | undefined, party: Party): string | undefined {

    if (!lineStatus?.editableBy || canEditInStatus(lineStatus, party)) return undefined;

    return lineStatus.statusType === EntityStatusType.Active ? 'This SOR is under review' : 'This SOR can no longer be changed';
}

// Work not yet authorised must not be recorded against the SOR task's sub-tasks; only a SOR can tick Work already completed to open them, so no other line is locked.
export function findPendingSorSubTaskRefusal(task: Pick<Task, 'parent'> | null | undefined, pendingLine: (Pick<SorItemFields, 'workAlreadyCompleted'> & { type?: CodeType }) | null | undefined): string | undefined {

    if (task?.parent?.type !== TaskLinkType.Item || !isSorCode(pendingLine) || pendingLine.workAlreadyCompleted) return undefined;

    return 'The sub-tasks of a pending SOR cannot be changed until it is approved or marked Work already completed';
}

// Scoped to live targets like the Request gate, so the office can still decline a Draft another side raised (WBS-11979).
// The office reviews every line, so it may request any Draft; a technician or subcontractor only their own (WBS-12206).
export function findAuthorRequestRefusal(from: EntityStatus | null | undefined, target: Pick<EntityStatus, '_id' | 'statusType' | 'isDefault'>, creator: ItemCreator | null | undefined, party: Party, companyId?: string): string | undefined {

    if (party === Party.Office) return undefined;
    if (target.statusType !== EntityStatusType.Active || target.isDefault || target._id === from?._id || !hasAuthorWindow(from)) return undefined;

    return canEditAsAuthor(creator, party, companyId) ? undefined : 'Only the side that raised this SOR may request it';
}
